import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ErrorNotification, Modal } from '@mr-yum/frontend-ui'
import { InputField } from 'components/HookForm/InputField'
import { useVenueContext } from 'contexts/VenueOrderContext'
import { useLogger } from 'hooks/useLogger'
import {
  useOptInToLoyaltyMutation,
  useUpdateCustomerDetailsMutation,
} from 'lib/gql'
import React, { useCallback, useRef } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import * as Yup from 'yup'

const getValidationSchema = ({ formatMessage }: IntlShape) =>
  Yup.object()
    .noUnknown(false)
    .shape({
      email: Yup.string()
        .email(
          formatMessage({
            id: 'qDmele',
            defaultMessage: 'Email must be valid',
          }),
        )
        .required(
          formatMessage({
            id: 'gtleS5',
            defaultMessage: 'Please provide your email address',
          }),
        ),
    })

type FormValues = {
  email: string
}

interface Props {
  isOpen: boolean
  email?: string | null
  minPoints?: number | null
  pointsData: {
    pointName: string
    points: string
    programName: string
  }
  onClose(): void
  onSuccess?(): void
}

export const LoyaltyFormModal = ({
  onClose,
  onSuccess,
  isOpen,
  email,
  minPoints = 0,
  pointsData,
}: Props) => {
  const { logEvent } = useLogger()
  const intl = useIntl()
  const form = useForm<FormValues>({
    defaultValues: {
      email: email ?? '',
    },
    mode: 'all',
    resolver: yupResolver(getValidationSchema(intl)),
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form
  const initialFocusRef = useRef(null)
  const { venueSlug } = useVenueContext()
  const [{ error: optInError }, sync] = useOptInToLoyaltyMutation()
  const [{ error: updateError }, updateUser] =
    useUpdateCustomerDetailsMutation()

  const submitHandler = useCallback<SubmitHandler<FormValues>>(
    async ({ email }) => {
      await updateUser({ email, venueSlug })
      await sync({
        venueSlug,
      })

      logEvent('Clicked get my points CTA on order confirmation (loyalty)', {
        minPoints,
      })

      if (!optInError && !updateError) {
        onClose?.()
        onSuccess?.()
      }
    },
    [
      optInError,
      logEvent,
      minPoints,
      onClose,
      onSuccess,
      venueSlug,
      sync,
      updateError,
      updateUser,
    ],
  )

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      initialFocusRef={initialFocusRef}
      title={
        <FormattedMessage defaultMessage="Add your email address" id="mP29w1" />
      }
    >
      <div className="p-4 text-foreground">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(submitHandler)}>
            {optInError && <ErrorNotification fullWidth error={optInError} />}
            {updateError && <ErrorNotification fullWidth error={updateError} />}

            <p className="mb-4 my-body-md">
              <FormattedMessage
                defaultMessage="Add your email address to join the {programName} program."
                id="ZicXjS"
                values={pointsData}
              />
            </p>
            <p className="mb-4 my-body-md">
              <FormattedMessage
                defaultMessage="By doing so, you also consent to receiving direct marketing from {programName}. You can opt out of either the loyalty program or direct marketing at any time."
                id="FFv55D"
                values={pointsData}
              />
            </p>

            <InputField
              autoFocus
              name="email"
              data-testid="email"
              label={intl.formatMessage({
                defaultMessage: 'Email address',
                id: 'hJZwTS',
              })}
            />

            <Button isLoading={isSubmitting} type="submit" fullWidth size="lg">
              <FormattedMessage defaultMessage="Sign up" id="8HJxXG" />
            </Button>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}
